/**
 * Scaffold inspection due-date tool (free tool,
 * /free/scaffold-inspection-due-date; SEO plan Workstream E row 8).
 *
 * Pure logic, no React and no clock: the visitor's answers and today's date
 * in, what inspection is needed and when out. Every word the visitor can see
 * lives here, so the page, the printable record and the safety review read
 * one set of text.
 *
 * Law (Work at Height Regulations 2005, read on legislation.gov.uk 31 Oct
 * 2026):
 *  - reg 12(2): equipment whose safety depends on how it is installed or
 *    assembled is not used after installation or assembly in any position
 *    unless it has been inspected in that position;
 *  - reg 12(3): equipment exposed to conditions causing deterioration is
 *    inspected at suitable intervals and each time exceptional circumstances
 *    liable to jeopardise its safety have occurred;
 *  - reg 12(4): a working platform used for construction work, from which a
 *    person could fall 2 metres or more, is not used in any position unless it
 *    has been inspected in that position (a mobile working platform: on the
 *    site) "within the previous 7 days";
 *  - reg 12(6), (7), (8) and Schedule 7: recording, the report and keeping it.
 * HSE's scaffolding page puts the same rule as "at an interval of no more than
 * every 7 days thereafter", so the last day a qualifying platform may be used
 * without a fresh inspection is the inspection date plus 7 days.
 *
 * The wording keeps to the approved scaffolding guide
 * (/guides/scaffolding-safety, pinned by
 * lib/guides/__tests__/scaffolding-safety-approved-content.test.ts): the rule
 * is tied to regulation 12(4), never to a flat "weekly" rule or to SG4.
 *
 * Pinned for every answer path in
 * __tests__/lib/marketing/scaffold-inspection/due-date.test.ts.
 */
import { formatUk } from "@/lib/dates";
import { HSE_SCAFFOLDING_URL, HSE_SCAFFOLD_FAQ_URL, WAHR_REG_12_URL, WAHR_SCHEDULE_7_URL } from "./sources";

export type Scope = "yes" | "no" | "unsure";
export type YesNo = "yes" | "no";
export type EventId = "altered" | "weather" | "impact" | "ground" | "other";

export interface DueDateAnswers {
  scope: Scope;
  inspected: YesNo;
  /** YYYY-MM-DD; asked only when inspected = yes. */
  lastInspected?: string;
  /** Asked only when inspected = yes; empty means none of these. */
  events?: readonly EventId[];
}

export interface Cite {
  label: string;
  url: string;
}

const REG_12 = (paragraph: string): Cite => ({
  label: `Work at Height Regulations 2005 reg 12(${paragraph})`,
  url: WAHR_REG_12_URL,
});
const HSE_PAGE: Cite = { label: "HSE, Scaffolding", url: HSE_SCAFFOLDING_URL };
const HSE_FAQ: Cite = { label: "HSE, Scaffolding FAQs", url: HSE_SCAFFOLD_FAQ_URL };
const SCHEDULE_7: Cite = { label: "Work at Height Regulations 2005 Schedule 7", url: WAHR_SCHEDULE_7_URL };

// ---------------------------------------------------------------------------
// Questions (exact wording shown to the visitor)
// ---------------------------------------------------------------------------

export const Q_SCOPE = {
  label: "Is it a working platform used for construction work, from which a person could fall 2 metres or more?",
  hint: "This is the platform that regulation 12(4) of the Work at Height Regulations 2005 covers.",
  options: [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "unsure", label: "I'm not sure" },
  ],
} as const;

export const Q_INSPECTED = {
  label: "Has it been inspected in position since it was put up?",
  options: [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No, not yet" },
  ],
} as const;

export const Q_LAST_INSPECTED = { label: "Date of the last inspection in position" } as const;

export const Q_EVENTS = {
  label: "Since that inspection, has any of these happened?",
  hint: "Tick all that apply. Leave them all unticked if none has.",
} as const;

export const EVENT_LABELS: Record<EventId, string> = {
  altered: "It has been altered, added to or partly taken down",
  weather: "High winds, a storm or other bad weather",
  impact: "It has been hit, for example by a vehicle or plant",
  ground: "Ground movement, digging or flooding near its footings",
  other: "Something else that could affect its safety",
};

export const EVENT_IDS: readonly EventId[] = ["altered", "weather", "impact", "ground", "other"];

// ---------------------------------------------------------------------------
// Outcomes
// ---------------------------------------------------------------------------

export type OutcomeId =
  | "inspect-before-first-use"
  | "inspect-after-event"
  | "overdue"
  | "due"
  | "baseline-overdue"
  | "baseline-due"
  | "future-date";

export interface Point {
  id: string;
  text: string;
  cite: Cite;
}

export interface DueDateResult {
  outcome: OutcomeId;
  /** The main line, shown large. */
  headline: string;
  /** YYYY-MM-DD of the latest date for the next routine inspection, when one applies. */
  dueDate: string | null;
  points: readonly Point[];
  /** Report and record-keeping points, shown with every valid result. */
  records: readonly Point[];
}

export const POINTS = {
  "first-use": {
    id: "first-use",
    text: "Regulation 12(2): where the safety of work equipment depends on how it is installed or assembled, it is not used after installation or assembly in any position unless it has been inspected in that position.",
    cite: REG_12("2"),
  },
  "after-event": {
    id: "after-event",
    text: "Inspect it again after any alteration, and after anything liable to affect its safety, such as high winds, an impact or ground movement. HSE says inspection may also be required after bad weather and always after any modification.",
    cite: REG_12("2) and 12(3"),
  },
  "seven-day-rule": {
    id: "seven-day-rule",
    text: "Regulation 12(4): a working platform used for construction work, from which a person could fall 2 metres or more, must not be used unless it has been inspected in that position within the previous 7 days. For a mobile working platform, the inspection is on the site.",
    cite: REG_12("4"),
  },
  "hse-interval": {
    id: "hse-interval",
    text: "HSE puts it as: inspect following installation and before first use, then at an interval of no more than every 7 days, and following any circumstances liable to jeopardise its safety, such as high winds.",
    cite: HSE_PAGE,
  },
  "maximum-not-target": {
    id: "maximum-not-target",
    text: "Seven days is the maximum gap, not a target. HSE notes that a risk assessment may call for more frequent inspection.",
    cite: HSE_FAQ,
  },
  "unsure-scope": {
    id: "unsure-scope",
    text: "You weren't sure whether the 7-day duty applies, so the result treats it as applying. Ask the scaffold contractor or your competent person to confirm.",
    cite: REG_12("4"),
  },
  "out-of-scope": {
    id: "out-of-scope",
    text: "Below the 2-metre threshold, or for equipment that isn't a construction working platform, the specific seven-day duty doesn't apply, but regulation 12(2) and (3) of the same Regulations still can. HSE's practical baseline is inspection before first use, at least every 7 days and after conditions likely to affect safety, and that is a sensible minimum.",
    cite: REG_12("2) and 12(3"),
  },
  competent: {
    id: "competent",
    text: "The inspection is a visual or more rigorous inspection by a competent person, including any testing appropriate for safety purposes.",
    cite: REG_12("10"),
  },
  // Records ------------------------------------------------------------------
  recorded: {
    id: "recorded",
    text: "Record the result of every inspection and keep it until the next inspection is recorded.",
    cite: REG_12("6"),
  },
  report: {
    id: "report",
    text: "For a platform covered by regulation 12(4), the person inspecting prepares a report with the Schedule 7 particulars before the end of the working period, and gives it, or a copy, to the person it was carried out for within 24 hours.",
    cite: REG_12("7"),
  },
  "report-kept": {
    id: "report-kept",
    text: "That report is kept at the site until the construction work is completed, and then at an office for 3 months.",
    cite: REG_12("8"),
  },
} as const satisfies Record<string, Point>;

export type PointId = keyof typeof POINTS;

/** The Schedule 7 particulars, word for word, printed as blank lines on the record. */
export const SCHEDULE_7_PARTICULARS: readonly string[] = [
  "The name and address of the person for whom the inspection was carried out.",
  "The location of the work equipment inspected.",
  "A description of the work equipment inspected.",
  "The date and time of the inspection.",
  "Details of any matter identified that could give rise to a risk to the health or safety of any person.",
  "Details of any action taken as a result of any matter identified in paragraph 5.",
  "Details of any further action considered necessary.",
  "The name and position of the person making the report.",
];
export const SCHEDULE_7_CITE = SCHEDULE_7;

export const NOT_LEGAL_ADVICE = "This is general guidance, not legal advice.";
export const SCOPE_LINE =
  "This tool covers Great Britain under the Work at Height Regulations 2005. Northern Ireland has its own Work at Height Regulations (Northern Ireland) 2005.";
export const NOT_THE_REPORT =
  "This record is not an inspection report. Only a competent person's inspection can show the scaffold is safe to use.";

// ---------------------------------------------------------------------------
// Dates (ISO YYYY-MM-DD, calendar arithmetic in UTC so no time zone can shift a day)
// ---------------------------------------------------------------------------

const ISO = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: string | undefined): value is string {
  if (!value || !ISO.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** "Friday 7 November 2026". Noon UTC is the same calendar day in London all year. */
export function longDate(iso: string): string {
  return formatUk(`${iso}T12:00:00Z`, { weekday: "long", day: "numeric", month: "long", year: "numeric" }, iso).replace(",", "");
}

/** The gap regulation 12(4) allows: inspected "within the previous 7 days". */
export const MAX_GAP_DAYS = 7;

export function isComplete(answers: Partial<DueDateAnswers>): answers is DueDateAnswers {
  if (answers.scope === undefined || answers.inspected === undefined) return false;
  if (answers.inspected === "no") return true;
  return isIsoDate(answers.lastInspected);
}

const pick = (ids: readonly PointId[]): Point[] => ids.map((id) => POINTS[id]);

const RECORDS: readonly PointId[] = ["recorded", "report", "report-kept"];

export function dueDate(answers: DueDateAnswers, today: string): DueDateResult {
  const covered = answers.scope !== "no";
  const scopePoints: PointId[] = answers.scope === "unsure" ? ["unsure-scope"] : [];
  const records = pick(RECORDS);

  if (answers.inspected === "no") {
    return {
      outcome: "inspect-before-first-use",
      headline: "Inspect it before anyone uses it.",
      dueDate: null,
      points: pick([...scopePoints, "first-use", ...(covered ? (["seven-day-rule"] as const) : (["out-of-scope"] as const)), "competent"]),
      records,
    };
  }

  const last = answers.lastInspected!;
  if (last > today) {
    return {
      outcome: "future-date",
      headline: "The date of the last inspection can't be after today. Check the date.",
      dueDate: null,
      points: [],
      records: [],
    };
  }

  if ((answers.events ?? []).length > 0) {
    return {
      outcome: "inspect-after-event",
      headline: "Inspect it again before anyone uses it.",
      dueDate: null,
      points: pick([...scopePoints, "after-event", ...(covered ? [] : (["out-of-scope"] as const)), "competent"]),
      records,
    };
  }

  const due = addDays(last, MAX_GAP_DAYS);
  const overdue = today > due;
  if (covered) {
    return overdue
      ? {
          outcome: "overdue",
          headline: "The last inspection is more than 7 days old. Don't let anyone work from it until it has been inspected again.",
          dueDate: due,
          points: pick([...scopePoints, "seven-day-rule", "hse-interval", "competent"]),
          records,
        }
      : {
          outcome: "due",
          headline: `Next inspection: on or before ${longDate(due)}. Don't let anyone work from it after that date unless it has been inspected again.`,
          dueDate: due,
          points: pick([...scopePoints, "seven-day-rule", "hse-interval", "maximum-not-target", "after-event", "competent"]),
          records,
        };
  }
  return overdue
    ? {
        outcome: "baseline-overdue",
        headline: "On HSE's baseline, the last inspection is more than 7 days old. Have it inspected before it is used again.",
        dueDate: due,
        points: pick(["out-of-scope", "competent"]),
        records,
      }
    : {
        outcome: "baseline-due",
        headline: `On HSE's baseline, the next inspection is on or before ${longDate(due)}.`,
        dueDate: due,
        points: pick(["out-of-scope", "after-event", "competent"]),
        records,
      };
}
